import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    getScopeLabel,
    getReviewStatusLabel,
    getTaskStatusLabel,
    getTaskTypeLabel,
    getRouteStatusLabel,
    getEvidenceStatusLabel,
    PMO_METRIC_LABELS,
} from "../utils/pmoLabels";
import {
    PMO_BADGE_TONES,
    getPmoTooltip,
    getTaskStatusTone,
    getReviewStatusTone,
    getRouteStatusTone,
    getEvidenceStatusTone,
    getHealthTone,
} from "../utils/pmoStyles";
import { API_BASE } from "../config/orbisConfig";



function safeNumber(value, fallback = 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function percent(value) {
    return Math.max(0, Math.min(100, safeNumber(value)));
}

function label(value) {
    return String(value || "sin estado")
        .replaceAll("_", " ")
        .replaceAll("-", " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(value) {
    if (!value) return "Sin fecha";

    const d = new Date(`${value}T00:00:00`);
    if (Number.isNaN(d.getTime())) return value;

    return d.toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
}

function initials(name = "Usuario") {
    return String(name)
        .trim()
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((x) => x[0]?.toUpperCase())
        .join("") || "U";
}

function healthStyle(health) {
    const h = String(health || "").toLowerCase();

    if (h === "rojo") {
        return {
            bg: "#FFF1F2",
            border: "#FECDD3",
            text: "#BE123C",
            accent: "#DC2626",
            label: "Crítico",
        };
    }

    if (h === "amarillo") {
        return {
            bg: "#FFFBEB",
            border: "#FDE68A",
            text: "#B45309",
            accent: "#F59E0B",
            label: "Atención",
        };
    }

    if (h === "verde") {
        return {
            bg: "#ECFDF5",
            border: "#A7F3D0",
            text: "#047857",
            accent: "#16A34A",
            label: "En ruta",
        };
    }

    return {
        bg: "#F8FAFC",
        border: "#E2E8F0",
        text: "#475569",
        accent: "#64748B",
        label: "Sin estado",
    };
}

function priorityStyle(priority) {
    const p = String(priority || "").toLowerCase();

    if (p === "critica") return { bg: "#FFF1F2", color: "#BE123C", border: "#FECDD3" };
    if (p === "alta") return { bg: "#FFFBEB", color: "#B45309", border: "#FDE68A" };
    if (p === "media") return { bg: "#EFF6FF", color: "#1D4ED8", border: "#BFDBFE" };

    return { bg: "#F8FAFC", color: "#475569", border: "#E2E8F0" };
}

function Pill({ children, tone = "neutral", title }) {
    const styles = PMO_BADGE_TONES;
    const s = styles[tone] || styles.neutral;
    const tooltip = title || getPmoTooltip(children);

    return (
        <span
            title={tooltip}
            style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "6px 9px",
                borderRadius: 999,
                background: s.bg,
                color: s.color,
                border: `1px solid ${s.border}`,
                fontSize: 10,
                fontWeight: 950,
                whiteSpace: "nowrap",
            }}
        >
            {children}
        </span>
    );
}


function getPriorityTooltip(priority) {
    const p = String(priority || "").toLowerCase();
    if (p === "critica") return "Prioridad crítica: requiere atención inmediata porque puede afectar fechas, entregables o decisiones clave del proyecto.";
    if (p === "alta") return "Prioridad alta: tarea importante para el avance del proyecto; debe atenderse con seguimiento cercano.";
    if (p === "media") return "Prioridad media: tarea relevante para la operación, sin nivel crítico o alto asignado.";
    if (p === "baja") return "Prioridad baja: tarea de seguimiento ordinario o bajo impacto operativo.";
    return "Nivel de prioridad operativo asignado a esta tarea.";
}

function getRoadmapTaskStatusTooltip(status) {
    const s = String(status || "").toLowerCase();
    if (s === "terminada") return "Terminada: la tarea quedó cerrada operativamente dentro del proyecto.";
    if (s === "en_proceso") return "En proceso: la tarea tiene trabajo activo o avance registrado, pero aún no está cerrada.";
    if (s === "pendiente") return "Pendiente: la tarea aún no inicia o no tiene avance operativo registrado.";
    if (s === "bloqueada") return "Bloqueada: la tarea no puede avanzar hasta resolver una dependencia, evidencia o condición operativa.";
    if (s === "cancelada") return "Cancelada: la tarea quedó fuera del alcance operativo y no debe afectar el avance activo.";
    return "Estado operativo actual de la tarea dentro del Roadmap.";
}

function getRoadmapReviewTooltip(reviewStatus, taskStatus, progress) {
    const r = String(reviewStatus || "").toLowerCase();
    const s = String(taskStatus || "").toLowerCase();
    const p = safeNumber(progress);

    if (!r || r === "borrador") return "Sin revisión activa: la tarea no tiene un flujo de revisión abierto en este momento.";
    if (r === "en_revision" || r === "pendiente_revision") return "En revisión: el avance fue enviado y está pendiente de decisión del revisor.";
    if (r === "aprobado" && s !== "terminada") return "Aprobado parcial: el último avance fue validado, pero la tarea aún no está cerrada operativamente.";
    if (r === "aprobado" && s === "terminada") return "Aprobado: el trabajo fue validado y la tarea está cerrada.";
    if (r === "rechazado") return "Rechazado: el avance fue revisado y requiere corrección antes de continuar.";
    if (r === "con_cambios" || r === "solicitud_cambios") return "Cambios solicitados: el revisor pidió ajustes antes de aprobar el avance.";
    if (p >= 100 && s !== "terminada") return "Revisión pendiente de cierre: la tarea llegó al 100%, pero aún requiere validación o cierre formal.";
    return "Estado de revisión asociado al último ciclo de validación de la tarea.";
}

function getRoadmapProgressTooltip(task) {
    const progress = safeNumber(task?.progress_percent);
    const status = String(task?.status || "").toLowerCase();
    const review = String(task?.review_status || "").toLowerCase();

    if (progress >= 100 && status === "terminada") return "Avance 100%: la tarea está completada y cerrada operativamente.";
    if (progress >= 100 && review === "en_revision") return "Avance 100%: el trabajo fue completado y está pendiente de aprobación del revisor.";
    if (progress >= 100) return "Avance 100%: revisar si corresponde cerrar formalmente la tarea o enviarla a revisión.";
    if (progress > 0) return `Avance ${progress.toFixed(0)}%: progreso operativo registrado; la tarea aún sigue abierta.`;
    return "Avance 0%: la tarea aún no registra progreso operativo.";
}

function getRoadmapOverdueTooltip(task) {
    const days = safeNumber(task?.days_overdue);
    return `${days} día(s) vencida: la tarea superó su fecha objetivo y aún no está cerrada. Requiere cierre, reprogramación o justificación.`;
}

function KpiCard({ title, value, subtitle, color = "#0F172A", icon = "◈" }) {
    return (
        <div
            style={{
                borderRadius: 22,
                background: "#FFFFFF",
                border: "1px solid #E2E8F0",
                padding: 18,
                boxShadow: "0 18px 38px rgba(15,23,42,.06)",
                minHeight: 132,
                position: "relative",
                overflow: "hidden",
            }}
        >
            <div
                style={{
                    position: "absolute",
                    right: -28,
                    top: -28,
                    width: 92,
                    height: 92,
                    borderRadius: 999,
                    background: `${color}14`,
                }}
            />

            <div style={{ position: "relative", display: "flex", justifyContent: "space-between", gap: 12 }}>
                <div>
                    <div
                        style={{
                            color: "#64748B",
                            fontSize: 11,
                            fontWeight: 950,
                            textTransform: "uppercase",
                            letterSpacing: ".04em",
                        }}
                    >
                        {title}
                    </div>

                    <div
                        style={{
                            marginTop: 10,
                            color,
                            fontSize: 32,
                            fontWeight: 950,
                            letterSpacing: "-.05em",
                            lineHeight: 1,
                        }}
                    >
                        {value}
                    </div>

                    {subtitle ? (
                        <div
                            style={{
                                marginTop: 10,
                                color: "#64748B",
                                fontSize: 12,
                                fontWeight: 750,
                                lineHeight: 1.35,
                            }}
                        >
                            {subtitle}
                        </div>
                    ) : null}
                </div>

                <div
                    style={{
                        width: 42,
                        height: 42,
                        borderRadius: 16,
                        background: `${color}12`,
                        color,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 18,
                        fontWeight: 950,
                        border: `1px solid ${color}22`,
                    }}
                >
                    {icon}
                </div>
            </div>
        </div>
    );
}

function ProgressBar({ value, color = "#2563EB", label, rightLabel }) {
    const v = percent(value);

    return (
        <div>
            {(label || rightLabel) ? (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 12,
                        marginBottom: 7,
                    }}
                >
                    <span style={{ color: "#334155", fontSize: 12, fontWeight: 850 }}>
                        {label}
                    </span>
                    <span style={{ color: "#64748B", fontSize: 12, fontWeight: 900 }}>
                        {rightLabel || `${v.toFixed(0)}%`}
                    </span>
                </div>
            ) : null}

            <div
                style={{
                    height: 10,
                    borderRadius: 999,
                    background: "#E2E8F0",
                    overflow: "hidden",
                    border: "1px solid #E2E8F0",
                }}
            >
                <div
                    style={{
                        width: `${v}%`,
                        height: "100%",
                        borderRadius: 999,
                        background: color,
                        boxShadow: `0 8px 18px ${color}40`,
                    }}
                />
            </div>
        </div>
    );
}

function UserMini({ name }) {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <div
                style={{
                    width: 30,
                    height: 30,
                    borderRadius: 999,
                    background: "linear-gradient(135deg, #2563EB, #7C3AED)",
                    color: "#FFFFFF",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 10,
                    fontWeight: 950,
                    flex: "0 0 auto",
                }}
            >
                {initials(name)}
            </div>

            <span
                style={{
                    color: "#334155",
                    fontSize: 12,
                    fontWeight: 850,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
            >
                {name || "Sin responsable"}
            </span>
        </div>
    );
}

function RoadmapRail({ phases = [], selectedPhaseId, onSelectPhase }) {
    if (!phases.length) {
        return (
            <div
                style={{
                    border: "1px dashed #CBD5E1",
                    borderRadius: 22,
                    padding: 22,
                    background: "#FFFFFF",
                    color: "#64748B",
                    fontSize: 13,
                    fontWeight: 800,
                    textAlign: "center",
                }}
            >
                Este proyecto aún no tiene fases para mostrar en el mapa de ruta.
            </div>
        );
    }

    return (
        <div
            style={{
                borderRadius: 28,
                background: "#FFFFFF",
                border: "1px solid #E2E8F0",
                boxShadow: "0 18px 45px rgba(15,23,42,.06)",
                padding: 22,
                overflowX: "auto",
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "stretch",
                    gap: 0,
                    minWidth: Math.max(980, phases.length * 190),
                    position: "relative",
                    padding: "8px 0",
                }}
            >
                <div
                    style={{
                        position: "absolute",
                        left: 52,
                        right: 52,
                        top: 49,
                        height: 4,
                        borderRadius: 999,
                        background: "#E2E8F0",
                    }}
                />

                {phases.map((phase, idx) => {
                    const hs = healthStyle(phase.health);
                    const active = selectedPhaseId === phase.phase_id;
                    const progress = percent(phase.progress_percent);

                    return (
                        <button
                            key={phase.phase_id}
                            type="button"
                            onClick={() => onSelectPhase?.(phase.phase_id)}
                            style={{
                                flex: "1 0 180px",
                                border: 0,
                                background: "transparent",
                                cursor: "pointer",
                                textAlign: "center",
                                position: "relative",
                                padding: "0 8px",
                            }}
                        >
                            <div
                                style={{
                                    width: 54,
                                    height: 54,
                                    borderRadius: 18,
                                    margin: "0 auto",
                                    background: active ? hs.accent : hs.bg,
                                    color: active ? "#FFFFFF" : hs.text,
                                    border: `2px solid ${active ? hs.accent : hs.border}`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: 950,
                                    fontSize: 14,
                                    boxShadow: active
                                        ? `0 18px 34px ${hs.accent}33`
                                        : "0 10px 24px rgba(15,23,42,.06)",
                                    position: "relative",
                                    zIndex: 2,
                                }}
                            >
                                {phase.wbs_code}
                            </div>

                            <div
                                style={{
                                    marginTop: 12,
                                    borderRadius: 18,
                                    padding: 12,
                                    background: active ? hs.bg : "#F8FAFC",
                                    border: active ? `1px solid ${hs.border}` : "1px solid #E2E8F0",
                                    minHeight: 118,
                                }}
                            >
                                <div
                                    style={{
                                        color: "#0F172A",
                                        fontSize: 12,
                                        fontWeight: 950,
                                        lineHeight: 1.22,
                                        minHeight: 30,
                                    }}
                                >
                                    {phase.phase_name}
                                </div>

                                <div
                                    style={{
                                        marginTop: 8,
                                        color: hs.text,
                                        fontSize: 20,
                                        fontWeight: 950,
                                        letterSpacing: "-.04em",
                                    }}
                                >
                                    {progress.toFixed(0)}%
                                </div>

                                <div
                                    style={{
                                        marginTop: 7,
                                        height: 7,
                                        borderRadius: 999,
                                        background: "#E2E8F0",
                                        overflow: "hidden",
                                    }}
                                >
                                    <div
                                        style={{
                                            width: `${progress}%`,
                                            height: "100%",
                                            background: hs.accent,
                                            borderRadius: 999,
                                        }}
                                    />
                                </div>

                                <div
                                    style={{
                                        marginTop: 8,
                                        color: "#64748B",
                                        fontSize: 10,
                                        fontWeight: 850,
                                    }}
                                >
                                    {phase.completed_tasks}/{phase.tasks_count} tareas
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function PhaseDetail({ phase, onOpenTask }) {
    if (!phase) {
        return (
            <div
                style={{
                    borderRadius: 26,
                    background: "#FFFFFF",
                    border: "1px solid #E2E8F0",
                    padding: 22,
                    color: "#64748B",
                    fontSize: 13,
                    fontWeight: 800,
                    boxShadow: "0 18px 45px rgba(15,23,42,.06)",
                }}
            >
                Selecciona una fase para ver el detalle ejecutivo.
            </div>
        );
    }

    const hs = healthStyle(phase.health);
    const pr = priorityStyle(phase.priority);

    return (
        <section
            style={{
                borderRadius: 28,
                background: "#FFFFFF",
                border: `1px solid ${hs.border}`,
                boxShadow: "0 18px 45px rgba(15,23,42,.06)",
                padding: 22,
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 18,
                    alignItems: "flex-start",
                    marginBottom: 18,
                }}
            >
                <div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                        <Pill tone={getHealthTone(phase.health)}>
                            {hs.label}
                        </Pill>
                        <span
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "6px 9px",
                                borderRadius: 999,
                                background: pr.bg,
                                color: pr.color,
                                border: `1px solid ${pr.border}`,
                                fontSize: 10,
                                fontWeight: 950,
                                whiteSpace: "nowrap",
                            }}
                        >
                            Prioridad {label(phase.priority)}
                        </span>
                    </div>

                    <h2
                        style={{
                            margin: 0,
                            color: "#0F172A",
                            fontSize: 24,
                            fontWeight: 950,
                            letterSpacing: "-.04em",
                        }}
                    >
                        {phase.wbs_code} · {phase.phase_name}
                    </h2>

                    <p
                        style={{
                            margin: "8px 0 0",
                            color: "#64748B",
                            fontSize: 13,
                            lineHeight: 1.55,
                            fontWeight: 700,
                            maxWidth: 760,
                        }}
                    >
                        {phase.description || "Fase del proyecto sin descripción registrada."}
                    </p>
                </div>

                <div
                    style={{
                        minWidth: 150,
                        borderRadius: 22,
                        background: hs.bg,
                        border: `1px solid ${hs.border}`,
                        padding: 14,
                        textAlign: "center",
                    }}
                >
                    <div style={{ color: hs.text, fontSize: 34, fontWeight: 950, letterSpacing: "-.06em" }}>
                        {percent(phase.progress_percent).toFixed(0)}%
                    </div>
                    <div style={{ color: "#64748B", fontSize: 11, fontWeight: 900, textTransform: "uppercase" }}>
                        avance fase
                    </div>
                </div>
            </div>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                    gap: 12,
                    marginBottom: 18,
                }}
            >
                <MiniStat label="Tareas" value={phase.tasks_count} />
                <MiniStat label="Terminadas" value={phase.completed_tasks} color="#16A34A" />
                <MiniStat label="Vencidas" value={phase.overdue_tasks} color="#DC2626" />
                <MiniStat label="En revisión" value={phase.review_tasks} color="#7C3AED" />
                <MiniStat label="Sin revisor" value={phase.without_reviewer || 0} color="#F59E0B" />
                <MiniStat label="Sin evidencia" value={phase.evidence_missing} color="#F59E0B" />
                <MiniStat label="Sin geo" value={phase.geo_missing} color="#F59E0B" />
            </div>

            <div style={{ marginBottom: 18 }}>
                <ProgressBar
                    value={phase.completion_rate}
                    color={hs.accent}
                    label="Tasa de cierre de la fase"
                    rightLabel={`${safeNumber(phase.completion_rate).toFixed(0)}%`}
                />
            </div>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 12,
                    marginBottom: 18,
                }}
            >
                <InfoBox label="Responsable principal" value={phase.owner_name || "Sin responsable"} />
                <InfoBox label="Inicio" value={formatDate(phase.start_date)} />
                <InfoBox label="Fecha objetivo" value={formatDate(phase.due_date)} />
            </div>

            <div
                style={{
                    borderRadius: 22,
                    background: "#F8FAFC",
                    border: "1px solid #E2E8F0",
                    padding: 16,
                }}
            >
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 12,
                        marginBottom: 12,
                    }}
                >
                    <div>
                        <h3
                            style={{
                                margin: 0,
                                color: "#0F172A",
                                fontSize: 15,
                                fontWeight: 950,
                            }}
                        >
                            Componentes de la fase
                        </h3>
                        <div
                            style={{
                                color: "#64748B",
                                fontSize: 12,
                                fontWeight: 700,
                                marginTop: 3,
                            }}
                        >
                            Tareas hijas asociadas a esta etapa.
                        </div>
                    </div>
                </div>

                {phase.children?.length ? (
                    <div style={{ display: "grid", gap: 10 }}>
                        {phase.children.map((task) => (
                            <TaskLine key={task.task_id} task={task} onOpenTask={onOpenTask} />
                        ))}
                    </div>
                ) : (
                    <div
                        style={{
                            border: "1px dashed #CBD5E1",
                            borderRadius: 18,
                            padding: 16,
                            color: "#64748B",
                            fontSize: 12,
                            fontWeight: 800,
                            textAlign: "center",
                            background: "#FFFFFF",
                        }}
                    >
                        Esta fase no tiene tareas hijas; se evalúa como una fase individual.
                    </div>
                )}
            </div>
        </section>
    );
}

function MiniStat({ label, value, color = "#0F172A" }) {
    return (
        <div
            style={{
                borderRadius: 18,
                background: "#FFFFFF",
                border: "1px solid #E2E8F0",
                padding: 13,
                boxShadow: "0 10px 22px rgba(15,23,42,.04)",
            }}
        >
            <div
                style={{
                    color: "#64748B",
                    fontSize: 10,
                    fontWeight: 950,
                    textTransform: "uppercase",
                    letterSpacing: ".04em",
                }}
            >
                {label}
            </div>
            <div
                style={{
                    marginTop: 6,
                    color,
                    fontSize: 22,
                    fontWeight: 950,
                    letterSpacing: "-.04em",
                }}
            >
                {value}
            </div>
        </div>
    );
}

function InfoBox({ label, value }) {
    return (
        <div
            style={{
                borderRadius: 18,
                background: "#F8FAFC",
                border: "1px solid #E2E8F0",
                padding: 13,
            }}
        >
            <div
                style={{
                    color: "#64748B",
                    fontSize: 10,
                    fontWeight: 950,
                    textTransform: "uppercase",
                    letterSpacing: ".04em",
                }}
            >
                {label}
            </div>
            <div
                style={{
                    marginTop: 6,
                    color: "#0F172A",
                    fontSize: 13,
                    fontWeight: 900,
                    lineHeight: 1.3,
                }}
            >
                {value}
            </div>
        </div>
    );
}

function TaskLine({ task, onOpenTask }) {
    const overdue = safeNumber(task.is_overdue) === 1;
    const pr = priorityStyle(task.priority);
    const taskStatusLabel = getTaskStatusLabel(task.status);
    const taskStatusTone = getTaskStatusTone(task.status);
    const reviewLabel = getReviewStatusLabel(task.review_status, task.status);
    const reviewTone = getReviewStatusTone(task.review_status, task.status);

    return (
        <button
            type="button"
            onClick={() => onOpenTask?.(task)}
            style={{
                width: "100%",
                textAlign: "left",
                border: overdue ? "1px solid #FCA5A5" : "1px solid #E2E8F0",
                background: "#FFFFFF",
                borderRadius: 18,
                padding: 13,
                cursor: "pointer",
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) auto",
                gap: 12,
                alignItems: "center",
                boxShadow: overdue
                    ? "0 12px 24px rgba(220,38,38,.08)"
                    : "0 10px 22px rgba(15,23,42,.04)",
            }}
        >
            <div style={{ minWidth: 0 }}>
                <div
                    style={{
                        color: "#0F172A",
                        fontSize: 13,
                        fontWeight: 950,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                >
                    {task.wbs_code} · {task.task_name}
                </div>

                <div
                    style={{
                        marginTop: 6,
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        flexWrap: "wrap",
                    }}
                >
                    <UserMini name={task.owner_name} />
                    {task.reviewer_name ? (
                        <>
                            <span style={{ color: "#CBD5E1" }}>•</span>
                            <span title="Revisor asignado para validar avances, evidencias o cierre de esta tarea." style={{ color: "#64748B", fontSize: 11, fontWeight: 850 }}>
                                Revisa: {task.reviewer_name}
                            </span>
                        </>
                    ) : (
                        <>
                            <span style={{ color: "#CBD5E1" }}>•</span>
                            <span title="Sin revisor asignado: la tarea no tiene responsable formal de validación." style={{ color: "#B45309", fontSize: 11, fontWeight: 950 }}>
                                Sin revisor
                            </span>
                        </>
                    )}
                    <span style={{ color: "#CBD5E1" }}>•</span>
                    <span title="Periodo planeado de ejecución: fecha de inicio y fecha objetivo de la tarea." style={{ color: "#64748B", fontSize: 11, fontWeight: 800 }}>
                        {formatDate(task.start_date)} → {formatDate(task.due_date)}
                    </span>
                </div>
            </div>

            <div style={{ display: "flex", gap: 7, justifyContent: "flex-end", flexWrap: "wrap" }}>
                <span
                    title={getPriorityTooltip(task.priority)}
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "6px 9px",
                        borderRadius: 999,
                        background: pr.bg,
                        color: pr.color,
                        border: `1px solid ${pr.border}`,
                        fontSize: 10,
                        fontWeight: 950,
                        whiteSpace: "nowrap",
                    }}
                >
                    Prioridad {label(task.priority)}
                </span>

                {overdue ? (
                    <Pill tone="danger" title={getRoadmapOverdueTooltip(task)}>{safeNumber(task.days_overdue)} días vencida</Pill>
                ) : (
                    <Pill tone={taskStatusTone} title={getRoadmapTaskStatusTooltip(task.status)}>{taskStatusLabel}</Pill>
                )}

                {task.review_status ? (
                    <Pill tone={reviewTone} title={getRoadmapReviewTooltip(task.review_status, task.status, task.progress_percent)}>{reviewLabel}</Pill>
                ) : null}

                <Pill tone="info" title={getRoadmapProgressTooltip(task)}>{percent(task.progress_percent).toFixed(0)}%</Pill>
            </div>
        </button>
    );
}


function ScopeNotice({ scope, fallbackLabel, fallbackDescription }) {
    const labelText = scope?.label || fallbackLabel;
    const descriptionText = scope?.description || fallbackDescription;

    return (
        <div
            style={{
                margin: "0 0 18px",
                borderRadius: 20,
                background: "#FFFFFF",
                border: "1px solid #DBEAFE",
                padding: "14px 16px",
                boxShadow: "0 12px 28px rgba(37,99,235,.06)",
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
            }}
        >
            <div
                style={{
                    width: 34,
                    height: 34,
                    borderRadius: 14,
                    background: "#EFF6FF",
                    color: "#1D4ED8",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 950,
                    flex: "0 0 auto",
                }}
            >
                ⌖
            </div>
            <div>
                <div style={{ color: "#0F172A", fontSize: 13, fontWeight: 950 }}>
                    Alcance de la vista: {labelText}
                </div>
                <div style={{ marginTop: 4, color: "#64748B", fontSize: 12, fontWeight: 750, lineHeight: 1.45 }}>
                    {descriptionText}
                </div>
            </div>
        </div>
    );
}

export default function RoadmapPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [data, setData] = useState(null);
    const [selectedProjectId, setSelectedProjectId] = useState(() => {
        return searchParams.get("project_id") || "all";
    });


    const loadRoadmap = async () => {
        try {
            setLoading(true);
            setError("");

            const token = localStorage.getItem("cani_auth") || "";
            const qp = new URLSearchParams();

            if (selectedProjectId && selectedProjectId !== "all") {
                qp.set("project_id", selectedProjectId);
            }

            const response = await fetch(`${API_BASE}/pmo_roadmap.php${qp.toString() ? `?${qp.toString()}` : ""}`, {
                headers: {
                    Authorization: token ? `Bearer ${token}` : "",
                    "X-CANI-AUTH": token || "",
                },
            });

            const txt = await response.text();

            if (!txt || !txt.trim()) {
                throw new Error("Respuesta vacía del Roadmap PMO");
            }

            const json = JSON.parse(txt);

            if (!response.ok || !json.success) {
                throw new Error(json.message || "No se pudo cargar Roadmap PMO");
            }

            setData(json.data || null);
        } catch (e) {
            setError(e.message || "Error al cargar Roadmap PMO");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadRoadmap();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProjectId]);

    const phases = data?.phases || [];
    const selectedProject = data?.selected_project || null;
    const summary = data?.summary || {};
    const scope = data?.scope || {};
    const projectOptions = data?.project_options || data?.projects || [];
    const visibleProjectOptions = useMemo(() => {
        const seen = new Set();

        return (projectOptions || [])
            .filter((p) => {
                const id = String(p.project_id ?? p.id ?? "");

                if (!id || id === "0" || id.toLowerCase() === "all") {
                    return false;
                }

                if (seen.has(id)) {
                    return false;
                }

                seen.add(id);
                return true;
            })
            .map((p) => ({
                ...p,
                project_id: p.project_id ?? p.id,
                project_name: p.project_name ?? p.name,
                project_code: p.project_code ?? p.code,
            }));
    }, [projectOptions]);

    const isPortfolioView = selectedProjectId === "all" || !selectedProjectId;
    const headerProjectName = isPortfolioView
        ? "Todos los proyectos"
        : selectedProject?.project_name || "Sin proyecto";
    const headerProjectCode = isPortfolioView
        ? "Portafolio PMO"
        : selectedProject?.project_code || "Sin código";
    const officialProgressValue = selectedProject
        ? safeNumber(selectedProject.progress_percent)
        : safeNumber(summary.avg_progress);

    const [selectedPhaseId, setSelectedPhaseId] = useState(() => {
        const phaseId = searchParams.get("phase_id");
        return phaseId ? Number(phaseId) : null;
    });


    useEffect(() => {
        if (phases.length && !phases.some((p) => p.phase_id === selectedPhaseId)) {
            setSelectedPhaseId(phases[0].phase_id);
        }
    }, [phases, selectedPhaseId]);

    const selectedPhase = useMemo(() => {
        return phases.find((p) => p.phase_id === selectedPhaseId) || phases[0] || null;
    }, [phases, selectedPhaseId]);

    const openTask = (task) => {
        if (!task?.task_id) return;

        const returnParams = new URLSearchParams();

        if (selectedProjectId && selectedProjectId !== "all") {
            returnParams.set("project_id", selectedProjectId);
        } else if (selectedProject?.project_id && Number(selectedProject.project_id) > 0) {
            returnParams.set("project_id", selectedProject.project_id);
        }

        if (selectedPhaseId) {
            returnParams.set("phase_id", selectedPhaseId);
        }

        const returnTo = `/roadmap${returnParams.toString() ? `?${returnParams.toString()}` : ""}`;

        const qp = new URLSearchParams();
        qp.set("mode", "progress");
        qp.set("return_to", returnTo);
        qp.set("return_label", "Roadmap Ejecutivo");

        navigate(`/my-work/task/${task.task_id}?${qp.toString()}`);
    };


    const openProject = () => {
        if (!selectedProject?.project_id || Number(selectedProject.project_id) <= 0) return;
        navigate(`/projects/${selectedProject.project_id}`);
    };

    if (loading) {
        return (
            <div
                style={{
                    minHeight: "60vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#64748B",
                    fontSize: 14,
                    fontWeight: 850,
                }}
            >
                Cargando Roadmap Ejecutivo...
            </div>
        );
    }

    if (error) {
        return (
            <div
                style={{
                    borderRadius: 20,
                    background: "#FFF1F2",
                    border: "1px solid #FECDD3",
                    color: "#BE123C",
                    padding: 20,
                    fontSize: 14,
                    fontWeight: 850,
                }}
            >
                {error}
            </div>
        );
    }

    const projectHealth = healthStyle(selectedProject?.health_status);

    return (
        <div
            style={{
                minHeight: "100vh",
                background:
                    "radial-gradient(circle at top left, rgba(37,99,235,.10), transparent 28%), #F8FAFC",
            }}
        >
            <section
                style={{
                    marginBottom: 18,
                    borderRadius: 28,
                    padding: 26,
                    background:
                        "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 58%, #EEF2FF 100%)",
                    border: "1px solid #E2E8F0",
                    boxShadow: "0 20px 50px rgba(15,23,42,.07)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 20,
                }}
            >
                <div>
                    <div
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "7px 11px",
                            borderRadius: 999,
                            background: "#EFF6FF",
                            border: "1px solid #BFDBFE",
                            color: "#1D4ED8",
                            fontSize: 11,
                            fontWeight: 950,
                            letterSpacing: ".06em",
                            textTransform: "uppercase",
                            marginBottom: 14,
                        }}
                    >
                        <span
                            style={{
                                width: 8,
                                height: 8,
                                borderRadius: 999,
                                background: "#2563EB",
                            }}
                        />
                        Dirección / Sponsor / Stakeholders
                    </div>

                    <h1
                        style={{
                            margin: 0,
                            fontSize: 34,
                            lineHeight: 1.05,
                            color: "#0F172A",
                            fontWeight: 950,
                            letterSpacing: "-.05em",
                        }}
                    >
                        Roadmap Ejecutivo
                    </h1>

                    <p
                        style={{
                            margin: "10px 0 0",
                            color: "#64748B",
                            fontSize: 14,
                            lineHeight: 1.5,
                            maxWidth: 840,
                            fontWeight: 700,
                        }}
                    >
                        Mapa de ruta visual del proyecto completo por fases, avance, vencimientos, evidencia y salud ejecutiva.
                    </p>
                </div>

                <div
                    style={{
                        minWidth: 260,
                        padding: 16,
                        borderRadius: 22,
                        background: "#0F172A",
                        color: "#FFFFFF",
                        boxShadow: "0 18px 45px rgba(15,23,42,.22)",
                    }}
                >
                    <div
                        style={{
                            color: "#94A3B8",
                            fontSize: 11,
                            fontWeight: 900,
                            textTransform: "uppercase",
                        }}
                    >
                        {getScopeLabel(selectedProjectId)}
                    </div>

                    <div
                        style={{
                            marginTop: 8,
                            fontSize: 20,
                            fontWeight: 950,
                            lineHeight: 1.15,
                        }}
                    >
                        {headerProjectName}
                    </div>

                    <div
                        style={{
                            marginTop: 8,
                            display: "flex",
                            gap: 8,
                            flexWrap: "wrap",
                        }}
                    >
                        <span
                            style={{
                                display: "inline-flex",
                                padding: "6px 9px",
                                borderRadius: 999,
                                background: `${projectHealth.accent}22`,
                                color: "#FFFFFF",
                                border: `1px solid ${projectHealth.accent}66`,
                                fontSize: 10,
                                fontWeight: 950,
                            }}
                        >
                            {projectHealth.label}
                        </span>

                        <span
                            style={{
                                display: "inline-flex",
                                padding: "6px 9px",
                                borderRadius: 999,
                                background: "rgba(255,255,255,.08)",
                                color: "#CBD5E1",
                                border: "1px solid rgba(255,255,255,.12)",
                                fontSize: 10,
                                fontWeight: 950,
                            }}
                        >
                            {headerProjectCode}
                        </span>
                    </div>
                </div>
            </section>

            <ScopeNotice
                scope={scope}
                fallbackLabel="Proyecto completo"
                fallbackDescription="Muestra fases, tareas, avance, fechas, evidencia y salud ejecutiva del proyecto seleccionado."
            />

            <section
                style={{
                    marginBottom: 18,
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "center",
                    flexWrap: "wrap",
                }}
            >
                <select
                    value={selectedProjectId}
                    onChange={(e) => {
                        const value = e.target.value;
                        setSelectedProjectId(value);
                        setSelectedPhaseId(null);

                        const next = new URLSearchParams(searchParams);

                        if (value && value !== "all") {
                            next.set("project_id", value);
                        } else {
                            next.delete("project_id");
                        }

                        next.delete("phase_id");

                        setSearchParams(next);
                    }}
                    style={{
                        height: 42,
                        minWidth: 320,
                        borderRadius: 999,
                        border: "1px solid #E2E8F0",
                        background: "#FFFFFF",
                        padding: "0 16px",
                        color: "#0F172A",
                        fontSize: 13,
                        fontWeight: 800,
                        outline: "none",
                        boxShadow: "0 12px 25px rgba(15,23,42,.04)",
                    }}
                >
                    <option value="all">Portafolio PMO</option>
                    {visibleProjectOptions.map((p) => (
                        <option key={p.project_id} value={p.project_id}>
                            {p.project_name}
                        </option>
                    ))}
                </select>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button
                        type="button"
                        onClick={openProject}
                        disabled={isPortfolioView}
                        title={isPortfolioView ? "Selecciona un proyecto específico para abrir su Ficha 360." : "Abrir Ficha 360 del proyecto seleccionado."}
                        style={{
                            height: 42,
                            padding: "0 16px",
                            borderRadius: 999,
                            border: "1px solid #E2E8F0",
                            background: "#FFFFFF",
                            color: isPortfolioView ? "#94A3B8" : "#0F172A",
                            fontSize: 12,
                            fontWeight: 950,
                            cursor: isPortfolioView ? "not-allowed" : "pointer",
                            opacity: isPortfolioView ? 0.72 : 1,
                            boxShadow: "0 12px 25px rgba(15,23,42,.04)",
                        }}
                    >
                        Abrir Ficha 360
                    </button>

                    <button
                        type="button"
                        onClick={loadRoadmap}
                        style={{
                            height: 42,
                            padding: "0 16px",
                            borderRadius: 999,
                            border: "1px solid #0F172A",
                            background: "#0F172A",
                            color: "#FFFFFF",
                            fontSize: 12,
                            fontWeight: 950,
                            cursor: "pointer",
                            boxShadow: "0 14px 28px rgba(15,23,42,.18)",
                        }}
                    >
                        Actualizar
                    </button>
                </div>
            </section>

            <section
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
                    gap: 16,
                    marginBottom: 18,
                }}
            >
                <KpiCard
                    title={PMO_METRIC_LABELS.officialProgress}
                    value={`${officialProgressValue.toFixed(2)}%`}
                    subtitle={selectedProject ? "Registrado en Ficha 360" : "Promedio oficial del portafolio"}
                    color="#2563EB"
                    icon="↗"
                />
                <KpiCard
                    title="Fases WBS"
                    value={summary.total_phases || 0}
                    subtitle="Etapas ejecutivas detectadas"
                    color="#2563EB"
                    icon="↗"
                />
                <KpiCard
                    title={PMO_METRIC_LABELS.operationalProgress}
                    subtitle="Promedio visual por fases/tareas"
                    value={`${safeNumber(summary.avg_progress).toFixed(0)}%`}
                    color="#7C3AED"
                    icon="◉"
                />
                <KpiCard
                    title="Cierre operativo"
                    value={`${safeNumber(summary.completion_rate).toFixed(0)}%`}
                    subtitle={`${summary.completed_tasks || 0}/${summary.total_tasks || 0} tareas completadas`}
                    color="#16A34A"
                    icon="✓"
                />
                <KpiCard
                    title="Tareas vencidas"
                    value={summary.overdue_tasks || 0}
                    subtitle="Requieren atención ejecutiva"
                    color="#DC2626"
                    icon="!"
                />
                <KpiCard
                    title="En revisión"
                    value={summary.review_tasks || 0}
                    subtitle="Pendientes de aprobación"
                    color="#F59E0B"
                    icon="◐"
                />
            </section>

            <section style={{ marginBottom: 18 }}>
                <RoadmapRail
                    phases={phases}
                    selectedPhaseId={selectedPhase?.phase_id}
                    onSelectPhase={(phaseId) => {
                        setSelectedPhaseId(phaseId);

                        const next = new URLSearchParams(searchParams);

                        if (selectedProjectId && selectedProjectId !== "all") {
                            next.set("project_id", selectedProjectId);
                        } else {
                            next.delete("project_id");
                        }

                        if (phaseId) {
                            next.set("phase_id", phaseId);
                        } else {
                            next.delete("phase_id");
                        }

                        setSearchParams(next);
                    }}
                />
            </section>

            <PhaseDetail phase={selectedPhase} onOpenTask={openTask} />
        </div>
    );
}
